import ReactGA from 'react-ga4';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
/////////////////////////////////////////////////////////////
import { telegramWebApp } from '@/main';
import { useCustomDispatch, useCustomSelector } from '@/core/redux/hooks';
import {
	Drawer,
	DrawerContent,
	DrawerClose,
	DrawerFooter,
	DrawerHeader,
	DrawerTitle,
	Button,
	Close,
	CoinImage,
	DrawerDescription,
} from '@/app/shared/components';
import { HackService } from '@/core/services';
import {
	resetCountdownEnergyHack,
	setAvailableEnergyHack,
	setCounter,
	setCurrentEnergy,
	setDiamonds,
	setStickerWonDialog,
	setTotalCoinsGenerated,
} from '@/core/redux/slices';
import { BoostRefillResponse } from '@/core/interfaces';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface EnergyHackProps {
	isOpen: boolean;
	onClose: React.Dispatch<React.SetStateAction<boolean>>;
	image: string;
}

export const EnergyHack: React.FC<EnergyHackProps> = ({
	isOpen,
	onClose,
	image,
}: EnergyHackProps) => {
	/*********************
	// #region Hooks
	**********************/
	const dispatch = useCustomDispatch();
	const { t } = useTranslation();

	/*********************
	// #region Redux
	**********************/
	const { max_energy } = useCustomSelector((state) => state.counter);
	const { availableEnergyHack, countdownEnergyHack } = useCustomSelector(
		(state) => state.hacks
	);

	/*********************
	// #region Functions
	**********************/
	const handleRequestFullEnergy = async () => {
		telegramWebApp.HapticFeedback.impactOccurred('heavy');
		if (countdownEnergyHack > 0) {
			toast.warning(
				`${countdownEnergyHack} ${t('hacks.energyHack.secondsLeft')}`
			);
			return;
		}

		if (availableEnergyHack <= 0) {
			toast.warning(t('hacks.energyHack.noMoreHacks'));
			return;
		}

		toast.promise(HackService.fullEnergy(), {
			loading: t('general.loading'),
			success: (data: BoostRefillResponse) => {
				// Emit event to Google Analytics
				ReactGA.event({
					category: 'boosts',
					action: 'Refill energy hack',
				});

				//Update available energy hack
				dispatch(setAvailableEnergyHack(data.data.remainingAttempts));
				// Update current energy
				dispatch(setCurrentEnergy(max_energy));
				// Update counter
				dispatch(setCounter(data.currentCoins));
				// Update diamonds
				dispatch(setDiamonds(data.currentDiamonds));
				// Add cooldown
				dispatch(resetCountdownEnergyHack());
				// Set total coins
				dispatch(setTotalCoinsGenerated(data.totalCoinsEarned));
				// Check if has a gift
				if (data.isAwardWinning) {
					dispatch(setStickerWonDialog(true));
				}

				return t('hacks.energyHack.energyRefilled');
			},
			error: t('hacks.energyHack.energyRefilledError'),
		});
		onClose(false);
	};

	/*********************
	// #region Template
	**********************/
	return (
		<Drawer open={isOpen} onOpenChange={onClose}>
			<DrawerContent>
				<div className="mx-auto mt-4 h-2 w-[100px] rounded-full bg-muted" />
				<div className="mx-auto w-full max-w-sm ">
					<DrawerHeader>
						<DrawerTitle>
							<VisuallyHidden>
								{t('hacks.energyHack.title')}
							</VisuallyHidden>
							<DrawerClose className="right-5 text-right absolute">
								<Close fillcolor="#fff" />
							</DrawerClose>
						</DrawerTitle>
					</DrawerHeader>

					<DrawerDescription>
						<VisuallyHidden>
							{t('hacks.energyHack.description')}
						</VisuallyHidden>
					</DrawerDescription>

					<div className="mx-auto px-8">
						<img
							className="cursor-pointer mx-auto w-[100px] mb-6"
							src={image}
						/>

						<div className="flex flex-col text-center gap-3 mb-8">
							<p className="font-bold text-2xl">
								{t('hacks.energyHack.title')}
							</p>
							<p className="font-semibold text-sm text-white text-center">
								{t('hacks.energyHack.description')}
							</p>
							<p className="font-semibold text-sm text-white text-center">
								{t('hacks.available')}: {availableEnergyHack}
							</p>
						</div>

						<div className="flex my-4 justify-center">
							<CoinImage />
							<p className="text-lg font-bold">
								{t('general.free')}
							</p>
						</div>
					</div>

					<DrawerFooter>
						<DrawerClose asChild>
							<Button
								variant="primary"
								className="py-6 w-full text-sm rounded-lg"
								onClick={handleRequestFullEnergy}
							>
								{t('hacks.energyHack.refill')}
							</Button>
						</DrawerClose>
					</DrawerFooter>
				</div>
			</DrawerContent>
		</Drawer>
	);
};
